import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import CarCard from "@/components/CarCard";
import api from "@/lib/api";

const BODY_TYPES = ["All", "Sedan", "SUV", "Truck", "Wagon", "Coupe", "Van", "Hatchback"];
const PRICE_BUCKETS = [
  { label: "All Prices", value: "all" },
  { label: "Under $15k", value: "0-15000" },
  { label: "$15k – $25k", value: "15000-25000" },
  { label: "$25k – $40k", value: "25000-40000" },
  { label: "$40k+", value: "40000-" },
];

import { useSeo } from "@/lib/seo";

export default function Inventory() {
  useSeo(
    "Used Car Inventory | Pit Stop Motors — Nampa, Idaho",
    "Browse quality used cars, trucks, vans and SUVs for sale at Pit Stop Motors in Nampa, Idaho. Honest prices, no pressure."
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [bodyType, setBodyType] = useState(searchParams.get("body_type") || "All");
  const [bucket, setBucket] = useState(searchParams.get("price") || "all");

  const makes = useMemo(() => {
    const set = new Set(cars.map((c) => c.make));
    return ["All", ...Array.from(set).sort()];
  }, [cars]);
  const [make, setMake] = useState(searchParams.get("make") || "All");

  const fetchCars = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (bodyType !== "All") params.body_type = bodyType;
    if (make !== "All") params.make = make;
    if (bucket !== "all") {
      const [min, max] = bucket.split("-");
      if (min) params.min_price = min;
      if (max) params.max_price = max;
    }
    try {
      const r = await api.get("/cars", { params });
      setCars(r.data || []);
    } catch (e) {
      setCars([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCars();
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (bodyType !== "All") next.set("body_type", bodyType);
    if (make !== "All") next.set("make", make);
    if (bucket !== "all") next.set("price", bucket);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyType, make, bucket]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    fetchCars();
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (bodyType !== "All") next.set("body_type", bodyType);
    if (make !== "All") next.set("make", make);
    if (bucket !== "all") next.set("price", bucket);
    setSearchParams(next, { replace: true });
  };

  const clearFilters = async () => {
    setQ("");
    setBodyType("All");
    setMake("All");
    setBucket("all");
    setSearchParams({});
    setLoading(true);
    try {
      const r = await api.get("/cars");
      setCars(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  const activeFilterCount =
    (q ? 1 : 0) + (bodyType !== "All" ? 1 : 0) + (make !== "All" ? 1 : 0) + (bucket !== "all" ? 1 : 0);

  return (
    <div data-testid="inventory-page">
      {/* HEADER */}
      <section className="bg-primary text-primary-foreground" data-testid="inventory-header">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-16 md:py-20">
          <p className="overline !text-accent-bright">Inventory</p>
          <h1 className="mt-3 font-serif text-5xl md:text-6xl tracking-tight leading-none">Every car, every story.</h1>
          <p className="mt-5 max-w-xl text-primary-foreground/70 leading-relaxed">
            Hand-selected vehicles, inspected by our team, and priced fairly. Find the one made for your family.
          </p>
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="border-b border-soft bg-card sticky top-[81px] md:top-[89px] z-30" data-testid="inventory-filters">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-4 flex flex-wrap items-center gap-3">
          <form onSubmit={onSearchSubmit} className="flex-1 min-w-[220px] max-w-md relative" data-testid="inventory-search-form">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search make, model…"
              className="pl-9 rounded-sm"
              data-testid="inventory-search-input"
            />
          </form>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-sm gap-2" data-testid="filter-make-trigger">
                Make: <span className="font-medium">{make}</span> <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Make</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {makes.map((m) => (
                <DropdownMenuItem key={m} onSelect={() => setMake(m)} data-testid={`filter-make-${m}`}>
                  {m}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-sm gap-2" data-testid="filter-body-trigger">
                Type: <span className="font-medium">{bodyType}</span> <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Body Type</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {BODY_TYPES.map((b) => (
                <DropdownMenuItem key={b} onSelect={() => setBodyType(b)} data-testid={`filter-body-${b}`}>
                  {b}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-sm gap-2" data-testid="filter-price-trigger">
                <SlidersHorizontal className="h-3 w-3" />
                Price <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Price Range</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {PRICE_BUCKETS.map((p) => (
                <DropdownMenuItem key={p.value} onSelect={() => setBucket(p.value)} data-testid={`filter-price-${p.value}`}>
                  {p.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="rounded-sm gap-1 text-muted-foreground"
              data-testid="filter-clear-button"
            >
              <X className="h-3 w-3" /> Clear ({activeFilterCount})
            </Button>
          )}
        </div>
      </section>

      {/* GRID */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 py-12">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-muted-foreground" data-testid="inventory-count">
            {loading ? "Loading…" : `${cars.length} vehicle${cars.length === 1 ? "" : "s"} listed`}
          </p>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="aspect-[16/10] bg-secondary animate-pulse" />
            ))}
          </div>
        ) : cars.length === 0 ? (
          <div className="border border-dashed border-soft p-16 text-center" data-testid="inventory-empty">
            <p className="font-serif text-2xl text-primary">No cars match those filters.</p>
            <p className="mt-2 text-muted-foreground text-sm">Try widening your search.</p>
            <Button onClick={clearFilters} variant="outline" className="mt-6 rounded-sm">
              Clear filters
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="inventory-grid">
            {cars.map((c) => (
              <CarCard key={c.id} car={c} testIdPrefix="inventory-card" />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
